import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { detailMovies, categoryMovies } from '@/mocks/categoryData';
import { hotMovies, latestMovies } from '@/mocks/movies';

function getAllMovies() {
  const all = [...hotMovies, ...latestMovies];
  const catMovies = Object.values(categoryMovies).flat();
  return [...all, ...catMovies];
}

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movieId = Number(id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const movie = detailMovies[movieId];

  if (!movie) {
    const allMovies = getAllMovies();
    const fallback = allMovies.find((m) => m.id === movieId);

    if (!fallback) {
      return (
        <>
          <Navbar />
          <main className="min-h-screen pt-16 flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200">
                <i className="ri-film-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-lg">该影片暂无详细信息</p>
              <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 font-medium">
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </>
      );
    }

    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16">
          <section className="w-full bg-background-50 py-10 md:py-14">
            <div className="w-full px-4 md:px-6">
              <Breadcrumb items={[
                { label: '首页', href: '/' },
                { label: fallback.category.split(' / ')[0], href: `/category/${fallback.category.includes('动画') ? 'anime' : 'movie'}` },
                { label: fallback.title },
              ]} />
              <div className="flex flex-col lg:flex-row gap-8 mt-6">
                <div className="w-full lg:w-80 shrink-0">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                    <img
                      src={fallback.imageUrl}
                      alt={fallback.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                    {fallback.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 mb-5">
                    <span className="flex items-center gap-1 px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-sm font-medium">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill"></i>
                      </div>
                      {fallback.rating}
                    </span>
                    <span className="px-3 py-1 bg-background-200 text-foreground-600 rounded-full text-sm">{fallback.year}</span>
                    <span className="px-3 py-1 bg-background-200 text-foreground-600 rounded-full text-sm">{fallback.region}</span>
                    <span className="px-3 py-1 bg-background-200 text-foreground-600 rounded-full text-sm">{fallback.category}</span>
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                    被窝网电影为您提供高清流畅的在线观影体验。本片是被窝网电影平台上的热门内容，深受广大影迷喜爱。点击下方按钮即可开始观看，无需注册，无需会员，畅享高清画质。
                  </p>
                  <button className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-play-circle-fill"></i>
                      </div>
                      立即观看
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  const allMovies = getAllMovies();
  const relatedMovies = movie.relatedIds
    .map((rid) => allMovies.find((m) => m.id === rid))
    .filter(Boolean);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: movie.title,
    description: movie.description,
    author: { '@type': 'Organization', name: '被窝网电影' },
    datePublished: `${movie.year}-01-01`,
    publisher: { '@type': 'Organization', name: '被窝网电影', url: 'https://www.beiwomovie.com' },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.beiwomovie.com/' },
      { '@type': 'ListItem', position: 2, name: movie.category.split(' / ')[0], item: `https://www.beiwomovie.com/category/${movie.category.includes('动画') ? 'anime' : 'movie'}` },
      { '@type': 'ListItem', position: 3, name: movie.title },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      <main className="min-h-screen pt-16">
        {/* Hero Section */}
        <section className="w-full relative h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={movie.imageUrl}
            alt={movie.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent"></div>
        </section>

        {/* Content */}
        <section className="w-full bg-background-50 -mt-20 relative z-10 pb-14 md:pb-20">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: movie.category.split(' / ')[0], href: `/category/${movie.category.includes('动画') ? 'anime' : 'movie'}` },
              { label: movie.title },
            ]} />

            <div className="flex flex-col lg:flex-row gap-8 mt-6">
              {/* Poster */}
              <div className="w-full sm:w-64 lg:w-72 shrink-0 mx-auto lg:mx-0">
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={movie.posterUrl}
                    alt={movie.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-2">
                  {movie.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span className="flex items-center gap-1 px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-sm font-medium">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill"></i>
                    </div>
                    {movie.rating}
                  </span>
                  <span className="px-3 py-1 bg-background-200 text-foreground-600 rounded-full text-sm">{movie.year}</span>
                  <span className="px-3 py-1 bg-background-200 text-foreground-600 rounded-full text-sm">{movie.region}</span>
                  <span className="px-3 py-1 bg-background-200 text-foreground-600 rounded-full text-sm">{movie.category}</span>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        tag === '热门'
                          ? 'bg-accent-100 text-accent-700'
                          : tag === '新片'
                            ? 'bg-primary-100 text-primary-700'
                            : tag === '高分'
                              ? 'bg-secondary-100 text-secondary-800'
                              : 'bg-background-200 text-foreground-600'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Meta Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 p-5 bg-background-100 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="text-foreground-400 text-sm w-16 shrink-0">导演</span>
                    <span className="text-foreground-900 text-sm font-medium">{movie.director}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-foreground-400 text-sm w-16 shrink-0">主演</span>
                    <span className="text-foreground-900 text-sm font-medium">{movie.cast}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-foreground-400 text-sm w-16 shrink-0">地区</span>
                    <span className="text-foreground-900 text-sm font-medium">{movie.region}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-foreground-400 text-sm w-16 shrink-0">年份</span>
                    <span className="text-foreground-900 text-sm font-medium">{movie.year}</span>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">剧情简介</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{movie.description}</p>
                </div>

                {/* Action */}
                <button className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-play-circle-fill"></i>
                    </div>
                    立即观看
                  </div>
                </button>
              </div>
            </div>

            {/* Related Movies */}
            {relatedMovies.length > 0 && (
              <div className="mt-14">
                <div className="flex items-end justify-between mb-8">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    相关推荐
                  </h2>
                  <Link
                    to="/category/movie"
                    className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    查看更多
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line"></i>
                    </div>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {relatedMovies.map((rm) => rm && (
                    <Link
                      key={rm.id}
                      to={`/detail/${rm.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-200">
                        <img
                          src={rm.imageUrl}
                          alt={rm.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                          loading="lazy"
                        />
                        <div className="absolute top-2 right-2 flex items-center gap-0.5 px-2 py-0.5 bg-black/60 backdrop-blur-sm rounded-md text-yellow-400 text-xs font-medium">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-[10px]"></i>
                          </div>
                          {rm.rating}
                        </div>
                      </div>
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1 mb-1">
                        {rm.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
                        <span>{rm.year}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                        <span>{rm.region}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}